import {Action, createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {createCustomer, findCustomersLikeName, getCustomer, getCustomers, saveCustomer} from "./customersAPI";

export interface CreatedCustomer {
    name: string,
    active: boolean
}

export interface Customer {
    id: string,
    name: string,
    active: boolean
    created: Date | undefined
    lastModified: Date | undefined
}

export interface CustomerLookup {
    id: string,
    name: string
}

export interface CustomerState {
    status: 'idle' | 'loading' | 'failed'
    customerLookupStatus: 'idle' | 'loading' | 'failed'
    customers: Customer[]
    customerLookup: CustomerLookup[]
    errorMessage: string | undefined
    customer: Customer | undefined
}

const initialState: CustomerState = {
    status: 'idle',
    customerLookupStatus: 'idle',
    customers: [],
    customerLookup: [],
    errorMessage: undefined,
    customer: undefined
};

export const getCustomersAsync = createAsyncThunk(
    'customers',
    async ({apiToken}
               : { apiToken: string}) => {
        const response = await getCustomers(apiToken);
        return response.data;
    }
);

export const getCustomerAsync = createAsyncThunk(
    'customer',
    async ({apiToken, id}
               : { apiToken: string, id: string }) => {
        const response = await getCustomer(apiToken, id);
        return response.data;
    })

export const findCustomersLikeNameAsync = createAsyncThunk(
    'customerLookup',
    async (
        {apiToken, name}: { apiToken: string, name: string }) => {
        const response = await findCustomersLikeName(apiToken, name);
        return response.data;
    }
);

export const saveCustomerAsync = createAsyncThunk(
    'editCustomer',
    async ({data, apiToken}
               : { data: Customer, apiToken: string }) => {
        const response = await saveCustomer(data, apiToken);
        return response.data;
    }
)

export const createCustomerAsync = createAsyncThunk(
    'createCustomer',
    async ({data, apiToken}
               : { data: CreatedCustomer, apiToken: string }) => {
        const response = await createCustomer(data, apiToken);
        return response.data;
    }
)

export const customerSlice = createSlice({
    name: 'customers',
    initialState,
    reducers: {
        dummy: (_, __: Action) => {
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCustomersAsync.pending, (state) => {
                const newState = {...state};
                newState.status = 'loading';
                newState.customers = [];
                return newState;
            })
            .addCase(getCustomersAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.status = 'idle';
                newState.customers = action.payload;
                return newState
            })
            .addCase(getCustomersAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.errorMessage = action.error.message;
                newState.customers = []
                return newState;
            })
            .addCase(getCustomerAsync.pending, (state) => {
                const newState = {...state};
                newState.status = 'loading';
                newState.customer = undefined;
                return newState;
            })
            .addCase(getCustomerAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.status = 'idle';
                newState.customer = action.payload;
                return newState;
            })
            .addCase(getCustomerAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.errorMessage = action.error.message;
                newState.customer = undefined;
                return newState
            })
            .addCase(findCustomersLikeNameAsync.pending, (state, _) => {
                const newState = {...state};
                newState.customerLookupStatus = 'loading';
                newState.customerLookup = [];
                return newState;
            })
            .addCase(findCustomersLikeNameAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.customerLookupStatus = 'idle';
                newState.customerLookup = action.payload;
                return newState;
            })
            .addCase(findCustomersLikeNameAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.customerLookupStatus = 'failed';
                newState.errorMessage = action.error.message;
                newState.customerLookup = []
                return newState;
            })
            .addCase(saveCustomerAsync.pending, (state, action) => {
                const newState = {...state}
                newState.status = 'loading';
                return newState;
            })
            .addCase(saveCustomerAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.status = 'idle';
                return newState;
            })
            .addCase(saveCustomerAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.errorMessage = action.error.message;
                return newState;
            })
            .addCase(createCustomerAsync.pending, (state, action) => {
                const newState = {...state}
                newState.status = 'loading';
                return newState;
            })
            .addCase(createCustomerAsync.fulfilled, (state, action) => {
                const newState = {...state};
                newState.status = 'idle';
                return newState;
            })
            .addCase(createCustomerAsync.rejected, (state, action) => {
                const newState = {...state};
                newState.status = 'failed';
                newState.errorMessage = action.error.message;
                return newState;
            });
    }
});

export default customerSlice.reducer;
